/**
 * Things that leave the app: the share sheet, an in-app browser, the
 * clipboard. Every plugin here has a web implementation, so each call runs the
 * plugin on both platforms and turns a refusal into `false` / `null`.
 */

export interface ShareContent {
  readonly text?: string;
  readonly title?: string;
  readonly url?: string;
}

/**
 * The system share sheet. `false` when the user dismisses it or the platform
 * has none (desktop browsers without `navigator.share`).
 */
export async function share(content: ShareContent): Promise<boolean> {
  try {
    const { Share } = await import("@capacitor/share");
    if (!(await Share.canShare()).value) {
      return false;
    }
    await Share.share(content);
    return true;
  } catch {
    return false;
  }
}

/**
 * Opens a URL in a Custom Tab on Android, or a new tab on the web — never
 * inside the app's own WebView, which would navigate the app away.
 */
export async function openInBrowser(url: string): Promise<void> {
  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url });
}

export async function copyText(text: string): Promise<boolean> {
  try {
    const { Clipboard } = await import("@capacitor/clipboard");
    await Clipboard.write({ string: text });
    return true;
  } catch {
    return false;
  }
}

/** `null` when the clipboard is empty, not text, or reading is refused. */
export async function readText(): Promise<string | null> {
  try {
    const { Clipboard } = await import("@capacitor/clipboard");
    const { type, value } = await Clipboard.read();
    return type.startsWith("text") ? value : null;
  } catch {
    return null;
  }
}
